import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaGithub, FaLinkedinIn } from "react-icons/fa";
import { FiMail, FiDownload } from "react-icons/fi";

import { Bio, skills, education } from "../../data/constants";
import Portrait from "../../images/vivek-event-portrait.jpeg";
import Window from "../common/Window";
import Chip, { ChipRow } from "../common/Chip";
import Achievements from "./Achievements";
import {
  fadeUp,
  revealLine,
  stampIn,
  staggerContainer,
  viewport,
} from "../../utils/motion";
import {
  SectionWrap,
  SectionInner,
  SectionHead,
  Eyebrow,
  SectionTitle,
  MaskLine,
  RuleLabel,
} from "../common/Section";

// About / "Character Sheet". Two dell-1996 ribbon cards side by side: the
// portrait sheet on the left rail, the bio dossier on the right column
// (DESIGN.md:431-434). Everything square, hard-bordered, unshadowed.

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 340px) minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PhotoFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 12px;
`;

const Photo = styled.img`
  width: min(100%, 280px);
  aspect-ratio: 1;
  flex-shrink: 0;
  object-fit: cover;
  object-position: 50% 0%;
  display: block;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.24);
`;

const Plate = styled.div`
  text-align: center;
`;

const PlateName = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 20px;
  font-weight: 900;
  line-height: 1;
  color: #ffffff;
`;

const Prose = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.biome.about.ink};
  margin: 0;

  strong {
    font-weight: 700;
    color: #ffffff;
  }

  em {
    font-style: normal;
    font-weight: 700;
    color: ${({ theme }) => theme.orange};
  }
`;

// Education badge — dell-1996's `buy-a-dell-sticker` shape: square, hard
// bordered, bevelled.
const EduBadge = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 8px 12px;

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: ${({ theme }) => theme.orange};

  background: rgba(255, 130, 54, 0.1);
  border: 1px solid ${({ theme }) => theme.orange};
  box-shadow: ${({ theme }) => theme.bevelSm} ${({ theme }) => theme.frameInk};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  margin: 12px 0 10px;
`;

const MicroLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.orange};
  margin-bottom: 8px;
`;

const IconRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 0;
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: ${({ theme }) => theme.biome.about.ink};
  font-size: 15px;
  transition:
    background 120ms linear,
    color 120ms linear;

  &:hover {
    background: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const ResumeLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 12px;
  border-radius: 0;
  border: 1px solid ${({ theme }) => theme.orange};
  background: transparent;
  color: ${({ theme }) => theme.orange};
  text-decoration: none;

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  transition:
    background 120ms linear,
    color 120ms linear;

  &:hover {
    background: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const Interests = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);

  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 13px;
  color: ${({ theme }) => theme.biome.about.muted};
`;


const socials = [
  { href: Bio.linkedin, label: "LinkedIn", Icon: FaLinkedinIn },
  { href: `mailto:${Bio.email}`, label: "Email", Icon: FiMail },
  { href: Bio.github, label: "GitHub", Icon: FaGithub },
];

const About = () => {
  const degree = education[0];

  return (
    <SectionWrap tone="about" id="about">
      <SectionInner>
        <SectionHead
          variants={staggerContainer(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
        >
          <Eyebrow tone="about" variants={fadeUp}>
            About
          </Eyebrow>
          <MaskLine>
            <SectionTitle tone="about" variants={revealLine}>
              Character Sheet
            </SectionTitle>
          </MaskLine>
        </SectionHead>

        <Grid>
          <Window label="Portrait" tone="about" pad="12px">
            <PhotoFrame>
              <Photo src={Portrait} alt={Bio.name} />
              <Plate>
                <PlateName>{Bio.name}</PlateName>
              </Plate>
            </PhotoFrame>
          </Window>

          <Window label="Profile" tone="about" pad="16px">
            <Prose>
              Hi, I'm <strong>{Bio.name}</strong>, an <em>AI engineer</em> with
              <strong> 1.5 years of experience</strong> building retrieval and
              agent systems that hold up in production. I
              work across <em>RAG Pipelines, Multi-Agent Orchestration, and
              Model Fine-Tuning</em>. What I care about most is evaluation:
              knowing systems work before anyone else has to rely on it.
            </Prose>

            {degree && (
              <EduBadge>
                {Bio.school} {Bio.schoolYears}
              </EduBadge>
            )}

            <Divider />

            <MicroLabel>Skills</MicroLabel>
            {/* Chips stamp in one after another — the section's busiest moment. */}
            <ChipRow
              as={motion.div}
              variants={staggerContainer(0.028)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              {skills.map((name) => (
                <Chip
                  key={name}
                  as={motion.span}
                  variants={stampIn}
                  $tone="about"
                >
                  {name}
                </Chip>
              ))}
            </ChipRow>

            <Divider />

            <MicroLabel>Connect</MicroLabel>
            <IconRow
              as={motion.div}
              variants={staggerContainer(0.06)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              {socials.map(({ href, label, Icon }) => (
                <IconLink
                  key={label}
                  as={motion.a}
                  variants={stampIn}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon />
                </IconLink>
              ))}
              <ResumeLink
                as={motion.a}
                variants={stampIn}
                href={Bio.resume}
                target="_blank"
                rel="noreferrer"
              >
                <FiDownload /> Resume
              </ResumeLink>
            </IconRow>

            <Interests>{Bio.interests.join("  ·  ")}</Interests>
          </Window>
        </Grid>

        <RuleLabel tone="about">Achievements Unlocked</RuleLabel>
        <Achievements />
      </SectionInner>
    </SectionWrap>
  );
};

export default About;
